<template>
  <page :headText="pageTitle">
    <template #rightBtn>
      <img src="@/assets/common/close-icon.svg" @click="goBack" />
    </template>
    <scan-processing v-if="isProcessingMobileScan" class="relative flex flex-1 flex-col" />
    <div v-else class="relative flex flex-1 flex-col">
      <model-viewer
        v-if="objToLoad && !isLoading"
        bg-white
        class="flex-1"
        style="max-height: 80vh;"
        :shader-type="shaderType"
        :obj-url="objToLoad"
        :fov="55"
        :measurement="currentMeasurement"
      />
      <template>
        <unit-switcher
          v-if="!measurementFailed"
          class="absolute top-0 left-0 right-0 mx-auto"
          :use-inch="useInch"
          :layout="'white'"
          @toggle="onUnitSwitch"
        />
        <!-- Left -->
        <measurement-bar
          v-if="leftMeasurements[0]"
          :measurements="leftMeasurements"
          :active-index="activeMeasurementIndex"
          :use-inch="useInch"
          :layout="'white'"
          @click="onMeasurementClick"
        />
        <!-- Right -->
        <measurement-bar
          v-if="rightMeasurements[0]"
          right
          :measurements="rightMeasurements"
          :active-index="activeMeasurementIndex - 5"
          :use-inch="useInch"
          :layout="'white'"
          @click="onMeasurementClick($event + 5)"
        />
      </template>
      <!-- Scan Time -->
      <scan-time
        :record="record"
        @click.native="toggleHistoryPanel"
        class="flex absolute bottom-0 mx-auto self-center"
      />
      <!-- History -->
      <rounded-card v-if="showHistory" height="auto" no-head>
        <history
          :records="records"
          :tid="tid"
          @choose="showRecord"
          @delete="deleteRecord"
          @close="closeHistory"
        />
      </rounded-card>
      <!-- Error Prompt -->
      <rounded-card v-if="showFailedPrompt" height="auto" no-head>
        <img
          class="absolute top-0 right-0 p-5"
          src="@/assets/common/close-icon.svg"
          @click="onFailedMobileScanPromptClosed"
        />
        <error is-mobile-scan />
        <div class="w-full flex mt-auto mb-5 justify-around">
          <c-button class="my-3" @click="retry">
            <span>{{ $t('result.scan_again') }}</span>
          </c-button>
        </div>
      </rounded-card>
    </div>
  </page>
</template>

<script>
import { mapActions, mapState } from 'vuex';
import { BODY_KIND, MOBILE_SCAN_PROVIDER, TG3D_MOBILE_SCAN_PROVIDER } from '@/store/modules/user';
import measurements from '@/mixins/measurements';
import ScanProcessing from '@/components/common/ScanProcessing.vue';
import ModelViewer from '@/components/common/ModelViewer.vue';
import UnitSwitcher from '@/components/common/UnitSwitcher.vue';
import MeasurementBar from '@/components/ShowResult/MeasurementBar.vue';
import RoundedCard from '@/components/common/RoundedCard.vue';
import Error from '@/components/ShowResult/Error.vue';
import ScanTime from '@/components/ShowResult/ScanTime.vue';
import History from '@/components/ShowResult/History.vue';
import CButton from '@/components/common/CButton.vue';
import Page from './Page.vue';

export default {
  mixins: [measurements],
  components: {
    Page,
    ScanProcessing,
    ModelViewer,
    UnitSwitcher,
    MeasurementBar,
    RoundedCard,
    Error,
    ScanTime,
    History,
    CButton,
  },
  props: {
    record: Object,
  },
  data: () => ({
    showFailedPrompt: false,
    showHistory: false,
    isLoading: false,
  }),
  methods: {
    ...mapActions('user', ['setObjUrlByTID', 'deleteScanRecord']),
    toggleHistoryPanel() {
      this.showHistory = true;
    },
    closeHistory() {
      this.showHistory = false;
    },
    showRecord(record) {
      this.$emit('show-record', record);
      this.closeHistory();
    },
    goBack() {
      this.$emit('back');
    },
    async deleteRecord(tid) {
      if (!tid) return;

      this.$events.startLoading();
      try {
        const isDeletingCurrent = tid === this.tid;
        const previousIndex = this.records.findIndex(r => r.tid === tid);
        await this.deleteScanRecord({ tid, kind: BODY_KIND });
        const newRecords = this.records;
        if (newRecords.length === 0) {
          // if no record available, go back to home page and make sure it's blank
          this.goBack();
        } else if (isDeletingCurrent) {
          // if the current one is deleted, try choosing the nearest (older) one
          this.$emit('show-record', this.records[previousIndex]);
        }
      } catch (e) {
        this.$events.alert(this.$t('welcome.unexpected_error'));
        console.log(`[ERROR] Failed to delete scan record: ${tid}, ${e?.message}`);
      } finally {
        this.$events.stopLoading();
      }
    },
    onFailedMobileScanPromptClosed() {
      if (this.objToLoad) {
        this.showFailedPrompt = false;
      } else {
        this.$emit('back');
      }
    },
    async getMeasurements() {
      try {
        const { data } = await this.$api.user.get(`/api/v1/scan_records/${this.tid}/size_xt`);
        this.processMeasurements(data);
      } catch (e) {
        console.log(e.message);
      }
    },
    onMeasurementClick(index) {
      this.activeMeasurementIndex = index;
      this.setCurrentMeasurement(this.measurements[index]);
    },
    retry() {
      this.$events.goto('Discovery');
    },
  },
  computed: {
    ...mapState('user', ['bodyRecords', 'objUrl', 'mobileScanObjUrl', 'shaderType']),
    records() {
      return this.bodyRecords;
    },
    objToLoad() {
      return this.isMobileScan ? this.mobileScanObjUrl : this.objUrl;
    },
    tid() {
      const { tid = '' } = this.record || {};
      return tid;
    },
    isMobileScan() {
      const { provider = 0 } = this.record || {};
      return provider === MOBILE_SCAN_PROVIDER || provider === TG3D_MOBILE_SCAN_PROVIDER;
    },
    isProcessingMobileScan() {
      const { status = 99 } = this.record || {};
      return this.isMobileScan && status < 3;
    },
    pageTitle() {
      return this.isProcessingMobileScan ? '' : this.$t('homePage.body_3d_avatar');
    },
  },
  watch: {
    measurementFailed: {
      handler(nv) {
        this.showFailedPrompt = nv;
      },
      immediate: true,
    },
    objToLoad: {
      handler(nv) {
        if (!nv && !this.isLoading) {
          this.measurementFailed = true;
        }
      },
      immediate: true,
    },
    tid: {
      async handler(nv) {
        this.showFailedPrompt = false;
        this.measurementFailed = false;
        this.$events.startLoading();
        this.isLoading = true;
        if (nv && !this.isProcessingMobileScan) {
          await this.getMeasurements();
        }
        await this.setObjUrlByTID(this.record);
        this.isLoading = false;
        this.$events.stopLoading();
      },
      immediate: true,
    },
  },
};
</script>
